import type { PendingForm } from '@/api/types/Forms'
import { ErrorState } from '@/components/ErrorState'
import { VisitCardDetail } from '@/components/VisitPage/VisitCardDetail'
import type { HomeTile } from '@/features/home/homeTiles'
import { Rise } from '@/ui/core/motion'
import { Note } from '@/ui/core/note'
import { Skeleton } from '@/ui/core/skeleton'
import { VStack } from '@/ui/core/stack'
import { HeroCard } from './HeroCard'
import { NothingHero } from './NothingHero'
import { ReferRow } from './ReferRow'
import { TilePair } from './TilePair'
import { TodoRow } from './TodoRow'

const VisitUnavailable = ({ onRetry }: { onRetry: () => void }) => (
	// In a note rather than on the page: Home is a stack of cards and everything else in it has a
	// surface, so a bare block here would read as the page having failed rather than one tile.
	<Note tone="white">
		<ErrorState
			fit="sheet"
			title="We couldn't load your visits"
			line="Check your connection and try again."
			onAction={onRetry}
		/>
	</Note>
)

/** A skeleton in the shape of the hero, so nothing jumps when the visit lands. */
const VisitLoading = () => (
	<Note tone="white" className="px-[18px] pt-[10px] pb-[18px]">
		<VStack gap={16}>
			<Skeleton isLoading className="h-[186px] w-full bg-blush" />
			<Skeleton isLoading className="h-3 w-20 bg-blush" />
			<Skeleton isLoading className="h-9 w-56 bg-blush" />
			<Skeleton isLoading className="h-11 w-48 bg-blush" />
		</VStack>
	</Note>
)

/**
 * The home, in the order the design fixes: a payment problem if there is one, the visit or the
 * empty visit, the to-dos as a row while there are any, the two standing tiles (the plan and the
 * kit), and referral as a row to close. New results are counted on the plan tile rather than
 * shown as a hero; the plan itself lives in Care.
 */
export const HomeTiles = ({
	tiles,
	onRetryVisits,
}: { tiles: HomeTile[]; onRetryVisits: () => void }) => {
	const forms: PendingForm[] = tiles.flatMap((tile) =>
		tile.kind === 'visit'
			? tile.pendingForms
			: tile.kind === 'todo-form'
			  ? [tile.form]
			  : []
	)
	const grid = tiles.find((tile) => tile.kind === 'grid')
	const refer = grid?.cards[2] ?? { id: 'refer' as const, state: 'loading' as const }
	const hero = tiles.find((tile) => tile.kind === 'hero')
	// New in the plan: results not yet opened. Recommendations join the count once the API has them.
	const planNew = hero?.hero === 'score-ready' ? 1 : 0
	const visit = tiles.find(
		(tile) =>
			tile.kind === 'visit' ||
			tile.kind === 'book-visit' ||
			tile.kind === 'visit-loading' ||
			tile.kind === 'visit-unavailable'
	)

	let index = 0
	const next = () => index++

	return (
		<VStack gap={12}>
			{hero?.hero === 'payment-attention' && (
				<Rise key="hero-payment" index={next()}>
					<HeroCard tile={hero} />
				</Rise>
			)}
			{visit?.kind === 'visit' && (
				<Rise key="visit" index={next()}>
					{/* The photograph as the card's head, then the two rows a member wants while
					    walking — where it is and the code that opens the building — and the one
					    thing to do about it. The photograph is also the way into the visit's own
					    page, which holds the walk and what to expect. */}
					<VisitCardDetail visit={visit.visit} />
				</Rise>
			)}
			{visit?.kind === 'book-visit' && (
				<Rise key="nothing" index={next()}>
					<NothingHero />
				</Rise>
			)}
			{visit?.kind === 'visit-loading' && <VisitLoading key="loading" />}
			{visit?.kind === 'visit-unavailable' && (
				<Rise key="unavailable" index={next()}>
					<VisitUnavailable onRetry={onRetryVisits} />
				</Rise>
			)}
			{/* Under the visit, not under the tiles. The to-dos are almost always *about* the
			    visit above them — the forms that visit is waiting on, matched to it by
			    `matchFormsToVisit` — and they sat below the plan and the kit, which are standing
			    tiles that never change. Nothing when there is nothing to do: an empty to-do row
			    is a chore invented by the app. */}
			{forms.length > 0 && (
				<Rise key="todo" index={next()}>
					<TodoRow forms={forms} />
				</Rise>
			)}
			<Rise key="pair" index={next()}>
				<TilePair planNew={planNew} />
			</Rise>
			<Rise key="refer" index={next()}>
				<ReferRow refer={refer} />
			</Rise>
		</VStack>
	)
}
